/* ---------------------------------------------------------------------------
   ACCESS

   There is no PIN and no code to type. Everybody gets in with just their email,
   and any email works. Typed codes were producing support tickets from people
   who could not open something they had already paid for, which is the worst
   possible first minute of a purchase.

   What a customer already owns travels invisibly in the delivery link instead:

     .../                              the kit on its own  (most buyers)
     .../?unlock=megapack              took the order bump
     .../?unlock=calligraphy           took the post-purchase upsell
     .../?unlock=megapack,calligraphy  took both
     .../?unlock=all                   everything, for your own testing

   Paste the matching URL into each product's "URL de entrega" in ImpulTienda.
   The customer clicks it, lands already unlocked, and never types anything.
--------------------------------------------------------------------------- */

export type PackId = 'megapack' | 'calligraphy' | 'videos'

export const ALL_PACKS: PackId[] = ['megapack', 'calligraphy', 'videos']

/* Words accepted in ?unlock=. The older CREATOR / PROKIT wording is kept on
   purpose: a delivery link is out in the world the moment you send it, and it
   has to keep working after we rename things. */
const ALIASES: Record<string, PackId[]> = {
  all: ALL_PACKS,
  everything: ALL_PACKS,
  prokit: ALL_PACKS,
  pro: ALL_PACKS,
  creator: ['megapack', 'calligraphy'],
  megapack: ['megapack'],
  papercraft: ['megapack'],
  origami: ['megapack'],
  calligraphy: ['calligraphy'],
  handwriting: ['calligraphy'],
  videos: ['videos'],
  video: ['videos'],
}

/* Reads ?unlock= from the delivery link. Anything unrecognised is ignored
   rather than treated as an error: a mangled link must never block a buyer,
   it just means they see the card locked and can ask us. */
export function unlockedFromUrl(search: string): PackId[] {
  try {
    const raw = new URLSearchParams(search).get('unlock')
    if (!raw) return []
    const found = new Set<PackId>()
    raw
      .toLowerCase()
      .split(/[,\s+|-]+/)
      .forEach((token) => {
        const key = token.replace(/[^a-z]/g, '')
        ALIASES[key]?.forEach((pack) => found.add(pack))
      })
    return [...found]
  } catch {
    return []
  }
}

export function mergePacks(a: PackId[], b: PackId[]): PackId[] {
  return [...new Set([...a, ...b])]
}

export function accessLabel(unlocked: PackId[]): string {
  if (ALL_PACKS.every((pack) => unlocked.includes(pack))) return 'Full Workshop'
  if (unlocked.length) return 'Mega Kit +'
  return 'Mega Kit'
}

/* ---------------------------------------------------------------------------
   CHECKOUT LINKS for the locked cards.

   These prices are deliberately HIGHER than at checkout. The checkout is the
   cheap moment; this is the second-chance moment. Do not match the two numbers.

     MegaPack Creative Hands      $9.90 as the order bump      ->  $19.90 here
     Mindful Handwriting          $14.90 as the post-purchase  ->  $24.90 here

   Each href wants its own ImpulTienda funnel, so the sale is measured
   separately and you can see what the portal earns on its own.
--------------------------------------------------------------------------- */
export interface UnlockOffer {
  href: string
  price: string
}

export const UNLOCK: Record<string, UnlockOffer> = {
  megapack: { href: '', price: '$19.90' },
  calligraphy: { href: '', price: '$24.90' },
  videos: { href: '', price: '$29' },
  everything: { href: '', price: '$39' },
}
